import { Donut } from '../insights/charts/Donut';
import { CrmCard } from './CrmCard';
import { formatRate } from './crmFormat';
import {
  ACCEPT_EDITED_COLOR,
  ACCEPT_SENT_COLOR,
  acceptRateModel,
  acceptRateByKind,
  NO_DRAFTS_SENT,
  type AcceptKindRow,
  type AcceptRateKpis,
} from './crmAcceptRateModel';

/**
 * v1 sends nothing on its own, so the accept rate is recorded as the evidence
 * for turning auto-send on in v2 (blueprint). Nothing else is claimed here:
 * the Figma frame's "Escalations flagged correctly · 100%" was static copy
 * with no data behind it.
 */
export const ACCEPT_RATE_TITLE = 'Agent accept-rate';

/** Says what is counted: drafts discarded or regenerated before sending never reach the rate (owner, 2026-10-16). */
export const ACCEPT_RATE_FOOTNOTE = 'Sent agent drafts only · evidence for auto-send in v2 · target 95%';

const DONUT_SIZE = 140;
const DONUT_THICKNESS = 20;

/** Agent drafts sent unedited ÷ agent drafts sent (Ruling 4: agent drafts only), with Figma's donut and legend. */
export function CrmAcceptRateCard({ kpis }: { kpis: AcceptRateKpis }) {
  const model = acceptRateModel(kpis);
  const sentAsDrafted = model.sentAsDraftedLabel;
  const editedBeforeSending = model.editedBeforeSendingLabel;

  return (
    <CrmCard title={ACCEPT_RATE_TITLE}>
      <div className="flex items-center justify-center py-2">
        <div role="img" aria-label={model.summary} className="relative flex items-center justify-center">
          <Donut segments={model.segments} size={DONUT_SIZE} thickness={DONUT_THICKNESS} />
          <div aria-hidden className="absolute flex flex-col items-center">
            <span className="text-2xl font-semibold tabular-nums text-foreground">{sentAsDrafted}</span>
            <span className="text-[11px] text-muted-foreground">Sent as drafted</span>
          </div>
        </div>
      </div>
      <NoDraftsNote measured={model.measured} />
      <dl className="space-y-1 text-sm">
        <LegendRow color={ACCEPT_SENT_COLOR} label="Sent as drafted" value={sentAsDrafted} />
        <LegendRow color={ACCEPT_EDITED_COLOR} label="Edited before sending" value={editedBeforeSending} />
      </dl>
      <AcceptRateByKind kpis={kpis} />
      <p className="text-xs text-muted-foreground">{ACCEPT_RATE_FOOTNOTE}</p>
    </CrmCard>
  );
}

/** The donut's own label already says this to screen readers, so the visible line is hidden from them. */
function NoDraftsNote({ measured }: { measured: boolean }) {
  if (measured) return null;
  return (
    <p aria-hidden="true" className="text-center text-xs text-muted-foreground">
      {NO_DRAFTS_SENT}
    </p>
  );
}

function LegendRow({ color, label, value }: { color: string; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="flex items-center gap-1.5 text-muted-foreground">
        <span aria-hidden className="size-2 rounded-full" style={{ background: color }} /> {label}
      </dt>
      <dd className="font-medium tabular-nums text-foreground">{value}</dd>
    </div>
  );
}

/** Ruling 4's per-kind split, collapsed to keep Figma's card clean. */
function AcceptRateByKind({ kpis }: { kpis: AcceptRateKpis }) {
  return (
    <details className="border-t border-border pt-2 text-sm">
      <summary className="cursor-pointer text-xs text-muted-foreground">By message kind</summary>
      <dl aria-label="By kind" className="mt-1 space-y-1">
        {acceptRateByKind(kpis).map((row) => (
          <AcceptKindItem key={row.key} row={row} />
        ))}
      </dl>
    </details>
  );
}

function AcceptKindItem({ row }: { row: AcceptKindRow }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted-foreground">{row.label}</dt>
      <dd className="text-right font-medium tabular-nums text-foreground">{formatRate(row.acceptRate)}</dd>
    </div>
  );
}
